import ApiClient from '../apiClient';

// Revshare Offer interfaces
export interface RevshareOffer {
  _id: string;
  of_user_id: number;
  of_user_name: string;
  name: string;
  avatar?: string;
  subscribers: number;
  overall_popularity_percent: number;
  likes: number;
  posts: number;
  favorited: number;
  created_at?: string;
  revshare_campaigns_count?: number;
  cpl_campaigns_count?: number;
  type: 'FREE' | 'FREE_TRIAL' | 'PAID' | 'free' | 'free trial';
  status?: 'active' | 'deactivated' | 'moderation'; // Статус аккаунта OnlyFans
  priority?: number; // Приоритет для сортировки (свой оффер = 1)
  owner:
    | {
        _id: string;
        name: string;
        avatar?: string;
        type: 'creator' | 'agency';
      }
    | string;
  of_data?: {
    email?: string;
    subscribers?: number;
    overall_popularity_percent?: number;
    updated_at?: string;
    balance?: string | number;
    favorited?: number;
    posts?: number;
    blocked_countries?: string[];
  };
  offer?: {
    commission: number;
    prohibited_sources: string[];
    show_service_info_for_partner: boolean;
    custom_media_for_partner: boolean;
    exist_chatters: boolean;
    chat_processing?: 'all_time' | 'custom_work_time' | 'ai' | 'not_specified';
    free_trial_access: boolean;
    description?: string;
    status:
      | 'MODERATION'
      | 'REFUSAL'
      | 'HIDDEN'
      | 'ACTIVE'
      | 'moderation'
      | 'refusal'
      | 'hidden'
      | 'active';
  };
  track_link?: {
    _id: string;
    link: string;
    track_link_id: number;
  };
  ref_share_active_total?: number;
  ref_share_transactions_total?: string | number;
  ref_share_subscribers_total?: number;
}

export interface RevshareOffersRequestParams {
  page?: number;
  page_size?: number;
  sort_by?: string;
  search?: string;
  type?: 'FREE' | 'FREE_TRIAL' | 'free' | 'free trial';
  performer_top?: number;
  commission?: number;
  guaranteed?: boolean;
  available_source_in?: string;
  available_source_out?: string;
  owner?: string;
  not_owner?: string;
  owner_type?: 'creator' | 'agency';
  models?: (string | number)[];
}

export interface RevshareOffersApiResponse {
  page: number;
  page_size: number;
  count: number;
  objects: RevshareOffer[];
}

// Revshare Order interfaces
export interface RevshareOrder {
  _id: string;
  created_at: string;
  type: 'free' | 'paid' | 'free_trial';
  source: string;
  subscribe_days: number;
  model: {
    id: string;
    link: string;
    track_link_id: number;
    of_user_name: string;
    of_user_id: number;
    avatar?: string;
    name: string;
    overall_popularity_percent?: number;
    blocked_countries?: string[];
    transitions_count?: number;
    public_content?: any;
    work_content?: any;
  };
  owner: {
    id: string;
    avatar?: string | null;
    name: string;
    lvl?: string;
  };
  status: 'in_progress' | 'in_moderation' | 'waiting' | 'completed' | 'canceled';
  offer: {
    commission: number;
    prohibited_sources: string[];
    show_service_info_for_partner: boolean;
    custom_media_for_partner: boolean;
    exist_chatters: boolean;
    chat_processing?: string;
    free_trial_access: boolean;
    description?: string;
    contact_for_content?: string;
    status: string;
    client?: {
      id: string;
      name: string;
      avatar?: string;
      ref_share_statistic?: any;
    };
  };
  subscribed_count: number;
  access_work_media?: any;
  transitions_count?: number | null;
  name?: string | null;
  description?: string | null;
  transactions_statistic: {
    '1': number;
    '2': number;
    '5': number;
    '500': number;
    total: string;
    count: number;
  };
  // Optional fields that should be added by backend
  verified_users_count?: number;
  repeated_subscriptions_count?: number;
  messages_3_plus_count?: number;
  subs?: {
    with_gte_3_chat_messages_count?: number;
    verified_count?: number;
  };
  // Legacy/computed fields for backward compatibility
  statistics?: {
    transactions_percent?: number;
    cost?: number;
    arpu?: number;
    arpu_usd?: number;
    subscribers?: number;
    revenue?: number;
    clicks?: number;
    clicks_percent?: number;
  };
}

export interface CreateRevshareOrderRequest {
  model_id: string;
  type: 'FREE' | 'FREE_TRIAL';
  source: string;
  subscribe_days?: number;
  expire_days?: number;
}

export interface RevshareOrdersApiResponse {
  page: number;
  page_size: number;
  count: number;
  objects: RevshareOrder[];
}

const clientRevshareService = {
  // Get list of available revshare offers
  getOffers: (params: RevshareOffersRequestParams = {}): Promise<RevshareOffersApiResponse> => {
    const searchParams = new URLSearchParams();

    searchParams.append('page', (params.page || 1).toString());
    searchParams.append('page_size', (params.page_size || 10).toString());
    searchParams.append('sort_by', params.sort_by || 'created_at');
    if (params.search) searchParams.append('search', params.search);
    if (params.type)
      searchParams.append('type', params.type.toString().replace('_', ' ').toLowerCase());
    if (params.performer_top !== undefined)
      searchParams.append('performer_top', params.performer_top.toString());
    if (params.commission !== undefined)
      searchParams.append('commission', params.commission.toString());
    if (params.guaranteed !== undefined)
      searchParams.append('guaranteed', params.guaranteed.toString());
    if (params.available_source_in)
      searchParams.append('available_source_in', params.available_source_in);
    if (params.available_source_out)
      searchParams.append('available_source_out', params.available_source_out);
    if (params.owner) searchParams.append('owner', params.owner);
    if (params.not_owner) searchParams.append('not_owner', params.not_owner);
    if (params.owner_type) searchParams.append('owner_type', params.owner_type);
    if (params.models && Array.isArray(params.models)) {
      params.models.forEach((modelId) => {
        if (modelId !== undefined && modelId !== null) {
          searchParams.append('models', modelId.toString());
        }
      });
    }

    return ApiClient.get(`/client/ref_share/offers?${searchParams.toString()}`).then(
      (response) => response.data
    );
  },

  // Create a new revshare order (request tracking link)
  createOrder: (data: CreateRevshareOrderRequest) => {
    return ApiClient.post(`/client/ref_share/orders`, data);
  },

  // Get list of client's revshare orders (campaigns)
  getOrders: (params: any = {}): Promise<RevshareOrdersApiResponse> => {
    const searchParams = new URLSearchParams();

    searchParams.append('page', (params.page || 1).toString());
    searchParams.append('page_size', (params.page_size || 10).toString());
    searchParams.append('sort_by', params.sort_by || 'created_at');

    // search -> id (backend field)
    if (params.search) searchParams.append('search', params.search);
    // models - array of model ids
    if (params.models && Array.isArray(params.models)) {
      params.models.forEach((modelId: string) => {
        searchParams.append('models', modelId.toString());
      });
    } else if (params.models) {
      searchParams.append('models', params.models.toString());
    }
    if (params.status) searchParams.append('status', params.status);
    if (params.source) searchParams.append('source', params.source);
    // subs_from - minimum subscribers
    if (params.subs_from !== undefined)
      searchParams.append('subs_from', params.subs_from.toString());
    // income - minimum revenue
    if (params.income !== undefined) searchParams.append('income', params.income.toString());
    // model_type filter
    if (params.model_type) searchParams.append('model_type', params.model_type);

    return ApiClient.get(`/client/ref_share/orders?${searchParams.toString()}`).then(
      (response) => response.data
    );
  },

  // Get list of client's revshare bills
  getBills: (params: any = {}) => {
    const searchParams = new URLSearchParams();

    searchParams.append('page', (params.page || 1).toString());
    searchParams.append('page_size', (params.page_size || 10).toString());

    // Search parameter
    if (params.search) searchParams.append('search', params.search);

    // Status filter
    if (params.status && params.status !== 'all') {
      searchParams.append('status', params.status);
    }

    // API parameters
    if (params.amount !== undefined && params.amount !== null) {
      searchParams.append('amount', params.amount.toString());
    }
    if (params.client_id) searchParams.append('client_id', params.client_id);
    if (params.company_id !== undefined && params.company_id !== null) {
      searchParams.append('company_id', params.company_id.toString());
    }
    if (params.inner_company_id) searchParams.append('inner_company_id', params.inner_company_id);
    if (params.partner_id) searchParams.append('partner_id', params.partner_id);
    if (params.model_id) searchParams.append('model_id', params.model_id);

    // Date range parameters (using double underscore as per backend schema)
    if (params.created_at__start)
      searchParams.append('created_at__start', params.created_at__start);
    if (params.created_at__end) searchParams.append('created_at__end', params.created_at__end);

    return ApiClient.get(`/client/ref_share/costs?${searchParams.toString()}`).then(
      (response) => response.data
    );
  },

  // Pay a cost from balance
  payBill: (costId: string) => {
    return ApiClient.post(`/client/ref_share/costs/pay/${costId}`, {}).then(
      (response) => response.data
    );
  },

  // Get bills statistics
  getBillsStats: () => {
    return ApiClient.get(`/client/ref_share/costs/stats`).then((response) => response.data);
  },
};

export default clientRevshareService;
